import { Box, Skeleton, Table, Text, Tooltip } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../../apis";
import { IArAgingCustomer, IArAgingReport } from "../../../apis/ledger";
import { message } from "../../../utils/Message";
import { Amt, money } from "./report-format";
import { saveBlob } from "./report-download";
import { periodBounds, periodKey, useReportPeriod } from "./report-period";
import { NoticeBar, ReportShell, StatusPill } from "./ReportShell";
import classes from "./ReportShell.module.css";
import { ListStates, resolveListStates } from "../../../components/list";

const COLS = 8;
const BUCKETS = ["bucketCurrent", "bucket1to30", "bucket31to60", "bucket61to90", "bucket91Plus"] as const;
const BUCKET_LABEL = ["colCurrent", "colBucket1to30", "colBucket31to60", "colBucket61to90", "colBucket91Plus"] as const;

const bucketSum = (c: IArAgingCustomer) => BUCKETS.reduce((s, k) => s + Number(c[k]), 0);
const cents = (n: number) => Math.round(n * 100);

/**
 * Авлагын насжилт (SPRINT-05 §6.5) — харилцагч бүрийн 5 бүлэг, AR дэд дэвтэр ба
 * 1200 данс хоёрын тулгалт. Бакетуудын нийлбэр үлдэгдэлтэй таарахгүй мөрийг
 * улаанаар тэмдэглэнэ.
 */
export const LedgerArAgingPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const navigate = useNavigate();
  const [period, setPeriod] = useReportPeriod();
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, error } = useSwr<IArAgingReport>(
    `ledger.report.arAging.${periodKey(period)}`,
    async () => await LedgerApi.getArAging(period),
    { revalidateOnFocus: false },
  );

  const customers = data?.customers ?? [];
  const reconciled = data ? cents(data.difference) === 0 : true;
  const totals = BUCKETS.map((k) => customers.reduce((s, c) => s + Number(c[k]), 0));
  const totalBalance = customers.reduce((s, c) => s + Number(c.currentBalance), 0);
  const bucketTotal = totals.reduce((s, n) => s + n, 0);
  const bucketGap = totalBalance - bucketTotal;
  const badRows = customers.filter((c) => cents(bucketSum(c)) !== cents(Number(c.currentBalance)));
  const asOf = periodBounds(period).to;
  const [y, m, d] = asOf.split("-").map(Number);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      saveBlob(await LedgerApi.exportReportExcel({ report: "ar-aging", ...period }), `ar-aging-${periodKey(period)}.xlsx`);
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const listStates = resolveListStates(
    <ListStates loading={isLoading} empty={customers.length === 0} columns={COLS} />
  );

  return (
    <ReportShell
      title={t("billing:ledger.report.agingTitle")}
      status={
        data && reconciled && badRows.length === 0 && (
          <StatusPill ok>{t("billing:ledger.report.reconciled")}</StatusPill>
        )
      }
      subtitle={t("billing:ledger.report.agingSubtitle", {
        year: y,
        month: t(`billing:ledger.report.month.${m}`),
        day: d,
      })}
      error={error}
      period={period}
      onPeriodChange={setPeriod}
      onExport={{ onExcel: handleExportExcel, excelLoading: exporting }}
      banner={
        data && (
          <>
            {!reconciled && (
              <NoticeBar
                title={t("billing:ledger.report.agingGapTitle", { amount: money(Math.abs(data.difference)) })}
                text={t("billing:ledger.report.agingGapText", {
                  subledger: money(data.subledgerTotal),
                  control: money(data.controlBalance),
                })}
                linkLabel={t("billing:ledger.report.toArReconciliation")}
                linkTo="/ledger/ar"
              />
            )}
            {badRows.length > 0 && (
              <NoticeBar
                tone="danger"
                title={t("billing:ledger.report.bucketGapTitle", { amount: money(Math.abs(bucketGap)) })}
                text={t("billing:ledger.report.bucketGapText", { count: badRows.length })}
              />
            )}
          </>
        )
      }
    >
      <Box className={classes.buckets}>
        {BUCKETS.map((k, i) => {
          const share = totalBalance > 0 ? (totals[i] / totalBalance) * 100 : 0;
          return (
            <Box key={k} className={`${classes.bucket} ${classes[`bucket${i}`]}`}>
              <Text span className={classes.bucketLabel}>
                <Box component="span" className={classes.bucketDot} />
                {t(`billing:ledger.report.${BUCKET_LABEL[i]}`)}
              </Text>
              <Text span className={classes.bucketAmount}>{data ? `${money(totals[i])} ₮` : "-"}</Text>
              <Box className={classes.bucketTrack}>
                <Box className={classes.bucketBar} style={{ "--bucket-width": `${Math.min(100, Math.max(0, share))}%` } as React.CSSProperties} />
              </Box>
              <Text span className={classes.bucketShare}>
                {t("billing:ledger.report.shareOfTotal", { pct: share.toFixed(1) })}
              </Text>
            </Box>
          );
        })}
      </Box>

      <Box className={classes.card}>
        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("billing:ledger.report.colCustomer")}</Table.Th>
                {BUCKET_LABEL.map((l) => (
                  <Table.Th key={l} className={classes.num}>{t(`billing:ledger.report.${l}`)}</Table.Th>
                ))}
                <Table.Th className={classes.num}>{t("billing:ledger.colBalance")}</Table.Th>
                <Table.Th className={classes.num}>{t("billing:ledger.report.colDso")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {customers.map((c) => {
                  const gap = Number(c.currentBalance) - bucketSum(c);
                  const bad = cents(gap) !== 0;
                  const go = () => navigate(`/customer-billing/${c.buyerBusinessId}`);
                  return (
                    <Table.Tr
                      key={c.buyerBusinessId}
                      className={classes.clickRow}
                      tabIndex={0}
                      role="link"
                      aria-label={c.buyerName}
                      onClick={go}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter") go();
                      }}
                    >
                      <Table.Td>
                        <Box className={classes.stack}>
                          <Text span className={classes.name}>{c.buyerName}</Text>
                          <Text span className={classes.code}>{c.buyerRefCode || c.buyerBusinessId.slice(0, 8)}</Text>
                        </Box>
                      </Table.Td>
                      {BUCKETS.map((k) => (
                        <Table.Td key={k} className={classes.num}>
                          {k === "bucket91Plus" && Number(c[k]) > 0 ? <Text span className={classes.negative}>{money(c[k])}</Text> : <Amt value={c[k]} />}
                        </Table.Td>
                      ))}
                      <Table.Td className={classes.num}>
                        {bad ? (
                          <Tooltip
                            withArrow
                            label={t("billing:ledger.report.bucketRowGap", { sum: money(bucketSum(c)), diff: money(Math.abs(gap)) })}
                          >
                            <Text span className={classes.negative}>{money(c.currentBalance)}</Text>
                          </Tooltip>
                        ) : (
                          <Text span className={classes.strong}>{money(c.currentBalance)}</Text>
                        )}
                      </Table.Td>
                      <Table.Td className={classes.num}>{c.dso}</Table.Td>
                    </Table.Tr>
                  );
                })}
            </Table.Tbody>
            {customers.length > 0 && !isLoading && (
              <Table.Tfoot>
                <Table.Tr className={classes.totalRow}>
                  <Table.Td>{t("billing:ledger.total")}</Table.Td>
                  {totals.map((n, i) => (
                    <Table.Td key={BUCKETS[i]} className={`${classes.num} ${i === 4 && n > 0 ? classes.negative : classes.strong}`}>
                      {money(n)}
                    </Table.Td>
                  ))}
                  <Table.Td className={`${classes.num} ${badRows.length > 0 ? classes.negative : classes.strong}`}>
                    {money(totalBalance)} ₮
                  </Table.Td>
                  <Table.Td />
                </Table.Tr>
              </Table.Tfoot>
            )}
          </Table>
          )}
        </Box>
      </Box>
    </ReportShell>
  );
};

export default LedgerArAgingPage;
